import React, { useState } from "react";
import {
    Save,
    MapPin,
    Calendar as CalendarIcon,
    Clock,
    AlertCircle,
    Info
} from "lucide-react";

export default function EventForm({ initialData = {}, onSubmit, isEditing = false, isLoading = false }) {
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        title: initialData.title || "",
        day: initialData.day || "",
        month: initialData.month || "JAN",
        year: initialData.year || new Date().getFullYear().toString(),
        time: initialData.time || "8:00 PM",
        location: initialData.location || "",
        venue: initialData.venue || "",
        description: initialData.description || "",
        status: initialData.status || "On Sale",
        ticket_url: initialData.ticket_url || "",
        ...initialData
    });

    const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    const statuses = ["On Sale", "Sold Out", "Free", "Completed"];

    const validate = () => {
        const newErrors = {};
        if (!formData.title?.trim()) newErrors.title = "Event title is required";
        if (!formData.time?.trim()) newErrors.time = "Event time is required";

        // Date Validation
        if (!formData.day?.trim()) {
            newErrors.day = "Day is required";
        } else {
            const day = parseInt(formData.day);
            const year = parseInt(formData.year);
            const monthIndex = months.indexOf(formData.month); // 0-11

            if (isNaN(day) || day < 1 || day > 31) {
                newErrors.day = "Invalid day";
            } else if (monthIndex !== -1 && !isNaN(year)) {
                // Get days in month (handling leap years for Feb)
                const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
                if (day > daysInMonth) {
                    newErrors.day = `Max ${daysInMonth} days in ${formData.month}`;
                } else {
                    // Future date check: must be tomorrow or later
                    const eventDate = new Date(year, monthIndex, day);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (eventDate <= today) {
                        newErrors.day = "Event must be in the future (tomorrow onwards)";
                    }
                }
            }
        }

        if (!formData.month) newErrors.month = "Month is required";
        if (!formData.year?.trim()) newErrors.year = "Year is required";
        if (!formData.venue?.trim()) newErrors.venue = "Venue is required";
        if (!formData.location?.trim()) newErrors.location = "Location is required";

        if (formData.description?.length > 2000) newErrors.description = "Description is too long";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Main Info */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <CalendarIcon size={20} className="text-jazz-brown" /> Event Details
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Event Title</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        placeholder="e.g., Jazz at Lincoln Center"
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.title ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                        required
                                    />
                                    {errors.title && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
                                            <AlertCircle size={16} />
                                        </div>
                                    )}
                                </div>
                                {errors.title && <p className="text-xs text-red-500 mt-1 font-medium">{errors.title}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Day (01-31)</label>
                                    <input
                                        type="text"
                                        name="day"
                                        value={formData.day}
                                        onChange={handleChange}
                                        placeholder="15"
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.day ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                        required
                                    />
                                    {errors.day && <p className="text-[10px] text-red-500 mt-1 font-medium">{errors.day}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Month</label>
                                    <select
                                        name="month"
                                        value={formData.month}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium appearance-none uppercase"
                                    >
                                        {months.map(m => (
                                            <option key={m} value={m}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Year</label>
                                    <input
                                        type="text"
                                        name="year"
                                        value={formData.year}
                                        onChange={handleChange}
                                        placeholder="2026"
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.year ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                        required
                                    />
                                    {errors.year && <p className="text-[10px] text-red-500 mt-1 font-medium">{errors.year}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Event Time</label>
                                    <div className="relative">
                                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40 dark:text-white/40" size={16} />
                                        <input
                                            type="time"
                                            name="time"
                                            value={(() => {
                                                // Convert stored "8:00 PM" to "20:00" for input
                                                if (!formData.time) return "";
                                                if (formData.time.includes(":")) {
                                                    const parts = formData.time.split(" ");
                                                    const timePart = parts[0];
                                                    const modifier = parts[1];

                                                    if (!modifier) return timePart; // Already 24h

                                                    let [hours, minutes] = timePart.split(":");
                                                    let h = parseInt(hours, 10);

                                                    if (modifier === "PM" && h < 12) h += 12;
                                                    if (modifier === "AM" && h === 12) h = 0;

                                                    return `${h.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                                                }
                                                return formData.time;
                                            })()}
                                            onChange={(e) => {
                                                // Convert "20:00" to "8:00 PM" for storage
                                                const val = e.target.value;
                                                if (val) {
                                                    let [hours, minutes] = val.split(":");
                                                    const h = parseInt(hours, 10);
                                                    const modifier = h >= 12 ? "PM" : "AM";
                                                    let displayHour = h % 12 || 12;
                                                    const newTime = `${displayHour}:${minutes} ${modifier}`;

                                                    // Init synthetic event to reuse existing handler
                                                    const synthEvent = {
                                                        target: { name: "time", value: newTime }
                                                    };
                                                    handleChange(synthEvent);
                                                } else {
                                                    handleChange(e);
                                                }
                                            }}
                                            className="w-full pl-10 pr-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Venue Name</label>
                                    <div className="relative">
                                        <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 ${errors.venue ? 'text-red-400' : 'text-black/40 dark:text-white/40'}`} size={16} />
                                        <input
                                            type="text"
                                            name="venue"
                                            value={formData.venue}
                                            onChange={handleChange}
                                            placeholder="e.g., Royal Albert Hall"
                                            className={`w-full pl-10 pr-4 py-3 rounded-xl border ${errors.venue ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                            required
                                        />
                                    </div>
                                    {errors.venue && <p className="text-xs text-red-500 mt-1 font-medium">{errors.venue}</p>}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Location (City, Country/State)</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleChange}
                                    placeholder="e.g., London, UK"
                                    className={`w-full px-4 py-3 rounded-xl border ${errors.location ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                    required
                                />
                                {errors.location && <p className="text-xs text-red-500 mt-1 font-medium">{errors.location}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Event Status</label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-bold uppercase tracking-widest appearance-none"
                                >
                                    {statuses.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">Ticket Purchase Link (Optional)</label>
                                <input
                                    type="url"
                                    name="ticket_url"
                                    value={formData.ticket_url}
                                    onChange={handleChange}
                                    placeholder="e.g., https://www.ticketmaster.com/event/123"
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black/70 dark:text-white/60 uppercase tracking-widest mb-2">
                                    Event Description <span className={`ml-2 text-[10px] ${formData.description.length > 2000 ? 'text-red-500' : 'text-black/50 dark:text-white/50'}`}>{formData.description.length}/2000</span>
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="Provide details about the performance, special guests, or repertoire..."
                                    className={`w-full px-4 py-3 rounded-xl border ${errors.description ? 'border-red-500 bg-red-50' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium resize-none transition-colors`}
                                />
                                {errors.description && <p className="text-xs text-red-500 mt-1 font-medium">{errors.description}</p>}
                            </div>
                        </div>
                    </div>

                </div>

                {/* Right Column: Actions & Meta */}
                <div className="space-y-6">
                    <div className="bg-black text-white p-6 lg:p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ fontFamily: "Playfair Display, serif" }}>
                            Publication Notes
                        </h3>
                        <div className="space-y-4 relative z-10">
                            <div className="flex gap-3">
                                <Info size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                    Changes will be reflected on the public Tour page immediately upon saving.
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <AlertCircle size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                    Use 'Sold Out' or 'Completed' to update event visibility.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-2 bg-black text-white py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10"
                        >
                            <Save size={18} /> {isEditing ? "UPDATE EVENT" : "PUBLISH EVENT"}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="w-full py-4 rounded-xl text-xs font-bold tracking-widest text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
